import React from "react";
import { Link } from "react-router-dom";
import { Pencil, Upload, Search, LifeBuoy, RefreshCw } from "lucide-react";
import { NextStep } from "@/types/chat.types";

const ICONS: Record<
  NextStep["kind"],
  React.ComponentType<{ className?: string }>
> = {
  refine: Pencil,
  upload: Upload,
  search: Search,
  support: LifeBuoy,
  retry: RefreshCw,
};

/**
 * Renders the actionable next steps offered when the assistant can't answer
 * (#24). Steps with an internal href navigate; external ones open a new tab;
 * advisory steps (rephrase / retry) render as muted hints.
 */
const NextSteps: React.FC<{ steps?: NextStep[] }> = ({ steps }) => {
  if (!steps || steps.length === 0) return null;

  return (
    <div className="mt-3 rounded-lg border border-subtle bg-surface-2 p-3">
      <p className="mb-2 text-xs font-semibold text-text-muted">
        Try one of these next
      </p>
      <div className="flex flex-col gap-1.5">
        {steps.map((step, i) => {
          const Icon = ICONS[step.kind] ?? Search;
          const inner = (
            <>
              <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{step.label}</span>
            </>
          );
          const base = "flex items-start gap-2 text-left text-sm";

          if (step.href && step.external) {
            return (
              <a
                key={i}
                href={step.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${base} text-accent hover:text-accent-strong hover:underline`}
              >
                {inner}
              </a>
            );
          }
          if (step.href) {
            return (
              <Link
                key={i}
                to={step.href}
                className={`${base} text-accent hover:text-accent-strong hover:underline`}
              >
                {inner}
              </Link>
            );
          }
          return (
            <div key={i} className={`${base} text-text-muted`}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NextSteps;
